import { Injectable } from '@angular/core';
import { INVALID_FILE_NAME_PATTERN, MIME_SUBTYPE_LABEL_ALIASES, MIME_TYPE_PATTERN } from '../const/file-extensions';

@Injectable({
  providedIn: 'root'
})
export class FileUtilsService {

  constructor() { }

  // Centralized map of common mime types to avoid duplication.
  private readonly MIME_TYPES: { [key: string]: string } = {
    // images
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml',

    // documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    docm: 'application/vnd.ms-word.document.macroEnabled.12',
    dot: 'application/msword',
    dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    dotm: 'application/vnd.ms-word.template.macroEnabled.12',
    odt: 'application/vnd.oasis.opendocument.text',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    csv: 'text/csv',
    txt: 'text/plain',
    xml: 'application/xml',
    json: 'application/json',
    log: 'text/plain',
    rtf: 'application/rtf',

    // video
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    mkv: 'video/x-matroska',
    avi: 'video/x-msvideo',
    webm: 'video/webm',

    // audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    aac: 'audio/aac',
    m4a: 'audio/mp4',

    // archives
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed'
  };

  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result as string);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Derives a short, user-facing file type label from a File object.
   * Prefers file extension and falls back to MIME subtype when needed.
   */
  getFileTypeLabel(file: File): string {
    const normalizedFileName = this.getFileNameValue((file as any)?.name);
    const extension = normalizedFileName.split('.').pop()?.trim();
    if (extension) {
      return extension.toUpperCase();
    }

    const mimeType = this.getMimeTypeValue(file.type);
    if (mimeType) {
      const [, subtype = ''] = mimeType.split('/');
      if (subtype) {
        return this.getShortSubtypeLabel(subtype);
      }
    }

    return 'FILE';
  }

  private getFileNameValue(nameValue: unknown): string {
    const candidates: string[] = [];

    if (typeof nameValue === 'string') {
      candidates.push(nameValue);
    }

    if (nameValue && typeof nameValue === 'object') {
      const source = nameValue as {
        name?: unknown;
        localURL?: unknown;
        nativeURL?: unknown;
        fullPath?: unknown;
        path?: unknown;
      };

      const sourceValues = [source.name, source.localURL, source.nativeURL, source.fullPath, source.path];
      sourceValues.forEach((value) => {
        if (typeof value === 'string' && value.trim().length > 0) {
          candidates.push(value);
        }
      });
    }

    for (const candidate of candidates) {
      const baseName = this.getBaseFileName(candidate);
      if (this.isUsableFileName(baseName)) {
        return baseName;
      }
    }

    return `attachment-${Date.now()}`;
  }

  private getBaseFileName(value: string): string {
    const normalizedValue = decodeURIComponent(value.trim()).replace(/[?#].*$/, '');
    if (!normalizedValue) {
      return `attachment-${Date.now()}`;
    }

    const slashIndex = Math.max(normalizedValue.lastIndexOf('/'), normalizedValue.lastIndexOf('\\'));
    const candidate = slashIndex >= 0 ? normalizedValue.slice(slashIndex + 1) : normalizedValue;
    return candidate.trim() || `attachment-${Date.now()}`;
  }

  private isUsableFileName(fileName: string): boolean {
    if (!fileName) {
      return false;
    }

    if (INVALID_FILE_NAME_PATTERN.test(fileName)) {
      return false;
    }

    return !/^[.\s]+$/.test(fileName);
  }

  private getMimeTypeValue(typeValue: unknown): string {
    if (typeof typeValue === 'string') {
      return this.normalizeMimeType(typeValue);
    }

    if (typeValue && typeof typeValue === 'object') {
      const source = typeValue as { type?: unknown; mimeType?: unknown; value?: unknown };
      const nestedCandidates = [source.type, source.mimeType, source.value];
      for (const nestedType of nestedCandidates) {
        const normalized = this.getMimeTypeValue(nestedType);
        if (normalized) {
          return normalized;
        }
      }
    }
    return '';
  }

  private normalizeMimeType(rawType: string): string {
    const cleanedType = rawType.trim().toLowerCase();
    if (!cleanedType || INVALID_FILE_NAME_PATTERN.test(cleanedType)) {
      return '';
    }

    if (!MIME_TYPE_PATTERN.test(cleanedType)) {
      return '';
    }

    return cleanedType;
  }

  private getShortSubtypeLabel(subtype: string): string {
    const normalizedSubtype = subtype.trim().toLowerCase();
    if (!normalizedSubtype) {
      return 'FILE';
    }

    const aliasLabel = MIME_SUBTYPE_LABEL_ALIASES[normalizedSubtype];
    if (aliasLabel) {
      return aliasLabel;
    }
    return normalizedSubtype.toUpperCase();
  }

  getFileSizeLabel(sizeInBytes: number): string {
    if (!Number.isFinite(sizeInBytes) || sizeInBytes < 0) {
      return '0 B';
    }

    if (sizeInBytes === 0) {
      return '0 B';
    }

    const sizeUnits = ['B', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(Math.floor(Math.log(sizeInBytes) / Math.log(1024)), sizeUnits.length - 1);
    const formattedSize = sizeInBytes / Math.pow(1024, unitIndex);
    const precision = formattedSize >= 10 || unitIndex === 0 ? 0 : 1;
    return `${formattedSize.toFixed(precision)} ${sizeUnits[unitIndex]}`;
  }

  /**
  * Return a mime type for a file extension if known, otherwise empty string.
  * Keep this in the service so other components can reuse the same map.
  */
  public getMimeTypeForExtension(extension: string): string {
    if (!extension) return '';
    return this.MIME_TYPES[extension.toLowerCase()] || '';
  }

}
